/**
 * Host-session persistence so a host keeps their game across a tab reload.
 *
 * The host ticket itself lives only in React state, so a reload used to drop it and
 * bounce the host back to the pack lobby (a fresh "pick a pack" screen) — losing the
 * game they were running. Players never had this problem: they persist their identity
 * and re-join on load. This is the host's equivalent.
 *
 * We persist the durable *handle* to the game — its `gameId` + `joinCode` — NOT the
 * ticket. On reload the host RE-MINTS a fresh ticket via the authenticated
 * `/ticket` path (M11.5.1): that call carries the creator's persisted guest token, so
 * only the original creator can restore the session, and it naturally surfaces
 * "game gone" (404) / "not the host" (403) as a re-mint failure — the caller then
 * clears the stale handle and shows the lobby, rather than livelocking a dead ticket.
 *
 * Scoped to prod (the deployed control plane); the dev server has a single auto-hosted
 * game with no lobby, so it needs none of this.
 */

const HOST_GAME_ID_KEY = 'jeopardy.host.gameId';
const HOST_JOIN_CODE_KEY = 'jeopardy.host.joinCode';

/** The persisted handle to a game this browser is hosting. */
export interface HostSession {
  gameId: string;
  joinCode: string;
}

/** Read the persisted host session, or null if none. */
export function loadHostSession(): HostSession | null {
  // SSR-safe: HostPage is prerendered under Node (#241), where `localStorage` is absent.
  if (typeof localStorage === 'undefined') return null;
  const gameId = localStorage.getItem(HOST_GAME_ID_KEY);
  const joinCode = localStorage.getItem(HOST_JOIN_CODE_KEY);
  if (!gameId || !joinCode) return null;
  return { gameId, joinCode };
}

/** Persist the host session so a reload can restore it. */
export function saveHostSession(session: HostSession): void {
  localStorage.setItem(HOST_GAME_ID_KEY, session.gameId);
  localStorage.setItem(HOST_JOIN_CODE_KEY, session.joinCode);
}

/** Drop the persisted host session (game gone / no longer the host / new game). */
export function clearHostSession(): void {
  localStorage.removeItem(HOST_GAME_ID_KEY);
  localStorage.removeItem(HOST_JOIN_CODE_KEY);
}
